import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

// Core values options (max 5 selectable)
const CORE_VALUES = [
  { id: 'value_quality', labelKey: 'quality' },
  { id: 'value_innovation', labelKey: 'innovation' },
  { id: 'value_sustainability', labelKey: 'sustainability' },
  { id: 'value_authenticity', labelKey: 'authenticity' },
  { id: 'value_creativity', labelKey: 'creativity' },
  { id: 'value_trust', labelKey: 'trust' },
  { id: 'value_luxury', labelKey: 'luxury' },
  { id: 'value_accessibility', labelKey: 'accessibility' },
  { id: 'value_tradition', labelKey: 'tradition' },
  { id: 'value_modernity', labelKey: 'modernity' },
  { id: 'value_community', labelKey: 'community' },
  { id: 'value_individuality', labelKey: 'individuality' },
]

// Industry options
const INDUSTRIES = [
  { id: 'industry_ecommerce', labelKey: 'ecommerce' },
  { id: 'industry_gastronomy', labelKey: 'gastronomy' },
  { id: 'industry_fashion', labelKey: 'fashion' },
  { id: 'industry_beauty', labelKey: 'beauty' },
  { id: 'industry_fitness', labelKey: 'fitness' },
  { id: 'industry_health', labelKey: 'health' },
  { id: 'industry_services', labelKey: 'services' },
  { id: 'industry_coaching', labelKey: 'coaching' },
  { id: 'industry_art', labelKey: 'art' },
  { id: 'industry_tech', labelKey: 'tech' },
  { id: 'industry_realestate', labelKey: 'realestate' },
  { id: 'industry_finance', labelKey: 'finance' },
  { id: 'industry_education', labelKey: 'education' },
  { id: 'industry_travel', labelKey: 'travel' },
  { id: 'industry_media', labelKey: 'media' },
  { id: 'industry_nonprofit', labelKey: 'nonprofit' },
  { id: 'industry_other', labelKey: 'other' },
]

// USP options
const USPS = [
  { id: 'usp_handmade', labelKey: 'handmade' },
  { id: 'usp_regional', labelKey: 'regional' },
  { id: 'usp_bio', labelKey: 'bio' },
  { id: 'usp_family', labelKey: 'family' },
  { id: 'usp_fast', labelKey: 'fast' },
  { id: 'usp_custom', labelKey: 'custom' },
  { id: 'usp_exclusive', labelKey: 'exclusive' },
  { id: 'usp_fair', labelKey: 'fair' },
  { id: 'usp_premium', labelKey: 'premium' },
  { id: 'usp_innovative', labelKey: 'innovative' },
  { id: 'usp_durable', labelKey: 'durable' },
  { id: 'usp_awarded', labelKey: 'awarded' },
  { id: 'usp_other', labelKey: 'other' },
]

// Personality spectrum definitions
const PERSONALITY_SPECTRUMS = [
  { id: 'serious_casual', leftKey: 'serious', rightKey: 'casual' },
  { id: 'classic_modern', leftKey: 'classic', rightKey: 'modern' },
  { id: 'reserved_confident', leftKey: 'reserved', rightKey: 'confident' },
]

/**
 * Multi-Select Chip Component with max selection limit
 */
function ChipSelect({ options, selected, onChange, translationPrefix, t, maxSelect = null }) {
  const toggleOption = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((item) => item !== id))
    } else {
      // Check max selection limit
      if (maxSelect && selected.length >= maxSelect) {
        return // Don't allow more selections
      }
      onChange([...selected, id])
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = selected.includes(option.id)
        const isDisabled = maxSelect && selected.length >= maxSelect && !isSelected
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => toggleOption(option.id)}
            disabled={isDisabled}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              isSelected
                ? 'bg-blue-600 text-white'
                : isDisabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t(`${translationPrefix}.${option.labelKey}`)}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Industry Dropdown with Search
 */
function IndustrySelect({ value, onChange, options, translationPrefix, t }) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filteredOptions = options.filter((option) => {
    const label = t(`${translationPrefix}.${option.labelKey}`)
    return label.toLowerCase().includes(search.toLowerCase())
  })

  const selectedLabel = value
    ? t(`${translationPrefix}.${options.find((o) => o.id === value)?.labelKey || 'other'}`)
    : t('wizard.steps.brand_values.industry.placeholder')

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2.5 text-left text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {selectedLabel}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-48">
            {filteredOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(option.id)
                  setIsOpen(false)
                  setSearch('')
                }}
                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 ${
                  value === option.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
              >
                {t(`${translationPrefix}.${option.labelKey}`)}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {t('common.noResults')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Personality Slider Component
 */
function PersonalitySlider({ id, value, onChange, leftLabel, rightLabel }) {
  const displayValue = value || 3 // Default to middle

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
      <div className="relative">
        <input
          type="range"
          min="1"
          max="5"
          value={displayValue}
          onChange={(e) => onChange(id, parseInt(e.target.value, 10))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        {/* Tick marks */}
        <div className="flex justify-between px-0.5 mt-1">
          {[1, 2, 3, 4, 5].map((tick) => (
            <div
              key={tick}
              className={`w-1.5 h-1.5 rounded-full ${
                tick === displayValue ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Brand Values Step Component
 *
 * Step 2 of the Brand Profile Wizard.
 * Collects: Core values (max 5), Industry, USPs, Personality sliders
 */
export default function BrandValuesStep({ data, onChange }) {
  const { t } = useTranslation('brandProfile')

  // Handle core value selection (max 5)
  const handleCoreValuesChange = useCallback(
    (core_values) => {
      onChange({ core_values })
    },
    [onChange]
  )

  // Handle industry change
  const handleIndustryChange = useCallback(
    (industry) => {
      onChange({ industry })
    },
    [onChange]
  )

  // Handle USP change
  const handleUspsChange = useCallback(
    (usps) => {
      // If "other" is deselected, clear the custom text
      if (!usps.includes('usp_other') && data.usps_other_text) {
        onChange({ usps, usps_other_text: '' })
      } else {
        onChange({ usps })
      }
    },
    [onChange, data.usps_other_text]
  )

  // Handle custom USP text change
  const handleUspsOtherTextChange = useCallback(
    (e) => {
      onChange({ usps_other_text: e.target.value })
    },
    [onChange]
  )

  // Handle personality slider change
  const handlePersonalityChange = useCallback(
    (sliderId, value) => {
      onChange({
        personality: {
          ...data.personality,
          [sliderId]: value,
        },
      })
    },
    [onChange, data.personality]
  )

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t('wizard.steps.brand_values.title')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('wizard.steps.brand_values.subtitle')}
        </p>
      </div>

      {/* Core Values */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('wizard.steps.brand_values.coreValues.label')}
        </label>
        <p className="text-xs text-gray-500 mb-3">
          {t('wizard.steps.brand_values.coreValues.hint', { max: 5 })}
        </p>
        <ChipSelect
          options={CORE_VALUES}
          selected={data.core_values}
          onChange={handleCoreValuesChange}
          translationPrefix="wizard.steps.brand_values.coreValues.options"
          t={t}
          maxSelect={5}
        />
        {/* Selection counter */}
        <div className="mt-2 text-xs text-gray-400">
          {data.core_values.length}/5 {t('common.selected')}
        </div>
      </div>

      {/* Industry */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('wizard.steps.brand_values.industry.label')}
        </label>
        <IndustrySelect
          value={data.industry}
          onChange={handleIndustryChange}
          options={INDUSTRIES}
          translationPrefix="wizard.steps.brand_values.industry.options"
          t={t}
        />
      </div>

      {/* USPs */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('wizard.steps.brand_values.usps.label')}
        </label>
        <ChipSelect
          options={USPS}
          selected={data.usps}
          onChange={handleUspsChange}
          translationPrefix="wizard.steps.brand_values.usps.options"
          t={t}
        />
        {/* Custom USP text input - shown when "other" is selected */}
        {data.usps.includes('usp_other') && (
          <div className="mt-3">
            <input
              type="text"
              value={data.usps_other_text || ''}
              onChange={handleUspsOtherTextChange}
              placeholder={t('wizard.steps.brand_values.usps.otherPlaceholder')}
              className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={200}
            />
          </div>
        )}
      </div>

      {/* Personality Sliders */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          {t('wizard.steps.brand_values.personality.label')}
        </label>
        <div className="space-y-6">
          {PERSONALITY_SPECTRUMS.map((spectrum) => (
            <PersonalitySlider
              key={spectrum.id}
              id={spectrum.id}
              value={data.personality?.[spectrum.id]}
              onChange={handlePersonalityChange}
              leftLabel={t(`wizard.steps.brand_values.personality.${spectrum.leftKey}`)}
              rightLabel={t(`wizard.steps.brand_values.personality.${spectrum.rightKey}`)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
